/**
 * The 10 km block grid — the same grid the report pipeline names its AOIs on.
 * Contract: goldfinder-llm-kb/.claude/skills/prospect-report/scripts/prospect.py
 *
 *   MGA51-368-6762   zone 51, south-west corner 368 000 mE, 6 762 000 mN
 *
 * This is a pure function of position on the MGA2020 grid, so the browser computes
 * the blocks in view rather than fetching them. There is no table, no WFS call and
 * nothing to keep in sync — an id means the same square here, in the pipeline, and
 * on a printed map, permanently. Roughly 25,000 blocks cover WA; fetching them as
 * one layer would be several megabytes, and none of it would be worth downloading.
 *
 * The transverse Mercator maths is inlined (about 40 lines) rather than pulling in
 * proj4: it is a fixed projection with fixed constants, and the pipeline's own
 * pyproj results are what the numbers below are checked against.
 */
export const GRID_M = 10000;

// GDA2020 / GRS80, the MGA parameters. Identical to GDA94/MGA for this purpose at
// the precision a 10 km block cares about.
const A = 6378137.0;
const F = 1 / 298.257222101;
const K0 = 0.9996;
const FE = 500000.0;
const FN = 10000000.0;

const E2 = F * (2 - F);
const EP2 = E2 / (1 - E2);
const N_ = F / (2 - F);

export const mgaZone = (lon) => Math.floor((lon + 180) / 6) + 1;
const zoneMeridian = (zone) => zone * 6 - 183;

const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

/** Meridional arc, series to n^4 — well inside a metre over WA. */
function M(phi) {
  const n2 = N_ * N_, n3 = n2 * N_, n4 = n3 * N_;
  const A0 = 1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256;
  const A2 = (3 / 8) * (E2 + (E2 * E2) / 4 + (15 * E2 * E2 * E2) / 128);
  const A4 = (15 / 256) * (E2 * E2 + (3 * E2 * E2 * E2) / 4);
  const A6 = (35 * E2 * E2 * E2) / 3072;
  void n2; void n3; void n4;
  return A * (A0 * phi - A2 * Math.sin(2 * phi) + A4 * Math.sin(4 * phi) - A6 * Math.sin(6 * phi));
}

/** lon/lat (degrees) -> MGA easting/northing (metres) for a given zone. */
export function toMGA(lon, lat, zone) {
  const phi = rad(lat);
  const dl = rad(lon - zoneMeridian(zone));
  const sp = Math.sin(phi), cp = Math.cos(phi), tp = Math.tan(phi);
  const nu = A / Math.sqrt(1 - E2 * sp * sp);
  const t2 = tp * tp, t4 = t2 * t2;
  const c = EP2 * cp * cp, c2 = c * c;
  const Adl = dl * cp, A2_ = Adl * Adl, A3 = A2_ * Adl, A4_ = A3 * Adl,
        A5 = A4_ * Adl, A6_ = A5 * Adl;

  const x = FE + K0 * nu * (Adl
    + (A3 / 6) * (1 - t2 + c)
    + (A5 / 120) * (5 - 18 * t2 + t4 + 14 * c - 58 * t2 * c));
  const y = FN + K0 * (M(phi)
    + nu * tp * (A2_ / 2
      + (A4_ / 24) * (5 - t2 + 9 * c + 4 * c2)
      + (A6_ / 720) * (61 - 58 * t2 + t4 + 270 * c - 330 * t2 * c)));
  return [x, y];
}

/** MGA easting/northing -> lon/lat (degrees). */
export function fromMGA(x, y, zone) {
  const m = (y - FN) / K0;
  const mu = m / (A * (1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256));
  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
  const phi1 = mu
    + ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu)
    + ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu)
    + ((151 * e1 ** 3) / 96) * Math.sin(6 * mu)
    + ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const sp = Math.sin(phi1), cp = Math.cos(phi1), tp = Math.tan(phi1);
  const c1 = EP2 * cp * cp, t1 = tp * tp;
  const n1 = A / Math.sqrt(1 - E2 * sp * sp);
  const r1 = (A * (1 - E2)) / (1 - E2 * sp * sp) ** 1.5;
  const d = (x - FE) / (n1 * K0);
  const d2 = d * d, d3 = d2 * d, d4 = d3 * d, d5 = d4 * d, d6 = d5 * d;

  const lat = phi1 - ((n1 * tp) / r1) * (d2 / 2
    - (d4 / 24) * (5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * EP2)
    + (d6 / 720) * (61 + 90 * t1 + 298 * c1 + 45 * t1 * t1 - 252 * EP2 - 3 * c1 * c1));
  const lon = (d
    - (d3 / 6) * (1 + 2 * t1 + c1)
    + (d5 / 120) * (5 - 2 * c1 + 28 * t1 - 3 * c1 * c1 + 8 * EP2 + 24 * t1 * t1)) / cp;
  return [zoneMeridian(zone) + deg(lon), deg(lat)];
}

/** The block id containing a point. */
export function cellOf(lon, lat) {
  const zone = mgaZone(lon);
  const [x, y] = toMGA(lon, lat, zone);
  return `MGA${zone}-${Math.floor(x / GRID_M) * 10}-${Math.floor(y / GRID_M) * 10}`;
}

/** A block id back to its corner ring and centre. Null if the id is not on the grid. */
export function cellBounds(cid) {
  // One digit is allowed on the easting: a block at a zone's western edge can sit
  // at 0-90 km, and demanding two silently dropped those blocks.
  const m = /^MGA(49|5[0-2])-(\d{1,4})-(\d{3,5})$/i.exec(String(cid).trim());
  if (!m) return null;
  const zone = +m[1], kme = +m[2], kmn = +m[3];
  // Off-grid ids are refused rather than snapped: a square that is not a block must
  // never come back wearing a block's name.
  if (kme % 10 || kmn % 10) return null;
  const x0 = kme * 1000, y0 = kmn * 1000;
  const ring = [[0, 0], [GRID_M, 0], [GRID_M, GRID_M], [0, GRID_M], [0, 0]]
    .map(([dx, dy]) => fromMGA(x0 + dx, y0 + dy, zone));
  return { cell: `MGA${zone}-${kme}-${kmn}`, zone, ring,
           mgaE: x0, mgaN: y0, centre: fromMGA(x0 + GRID_M / 2, y0 + GRID_M / 2, zone) };
}

/**
 * Every block whose centre falls inside a lon/lat window, as a FeatureCollection.
 * `cap` stops a zoomed-out viewport from building tens of thousands of polygons —
 * the caller should simply not draw the grid at that zoom.
 */
export function blocksIn(west, south, east, north, cap = 4000) {
  const features = [];
  for (let zone = mgaZone(west); zone <= mgaZone(east); zone += 1) {
    const corners = [[west, south], [west, north], [east, south], [east, north]]
      .map(([lo, la]) => toMGA(lo, la, zone));
    const xs = corners.map((c) => c[0]), ys = corners.map((c) => c[1]);
    for (let ix = Math.floor(Math.min(...xs) / GRID_M); ix <= Math.ceil(Math.max(...xs) / GRID_M); ix += 1) {
      for (let iy = Math.floor(Math.min(...ys) / GRID_M); iy <= Math.ceil(Math.max(...ys) / GRID_M); iy += 1) {
        // A viewport can reach outside the zone it is projected into, giving negative
        // eastings. Those belong to the neighbouring zone's own pass, not this one.
        if (ix < 0 || iy < 0) continue;
        const b = cellBounds(`MGA${zone}-${ix * 10}-${iy * 10}`);
        if (!b) continue;
        const [clon, clat] = b.centre;
        // A block belongs to the zone it was cut on; testing its centre against that
        // zone keeps the seam blocks from being drawn twice.
        if (clon < west || clon > east || clat < south || clat > north) continue;
        if (mgaZone(clon) !== zone) continue;
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [b.ring] },
          properties: { cell: b.cell, zone: b.zone, mgaE: b.mgaE, mgaN: b.mgaN },
        });
        if (features.length >= cap) return { type: 'FeatureCollection', features };
      }
    }
  }
  return { type: 'FeatureCollection', features };
}
